import { createSignal, For, Icon, onCleanup, type JSX } from 'cdui-js';
import { resultFailIcon as removeIcon } from '../../../assets/commonIcons';
import './difi-upload.css';
import { showToast } from '../../../pages/mobile/components/Toast';

const DEFAULT_LIST_TYPE = 'image';

export type DiFiUploadListType = 'text' | 'image' | 'image-card';

export type DiFiUploadMaxSizeRejectPayload = {
  maxBytes: number;
  files: File[];
};

export type DiFiUploadMaxCountExceedPayload = {
  limit: number;
  /** 因超出数量上限未被纳入的文件 */
  dismissed: File[];
  /** 处理完成后列表中的文件数量（list 模式）或本次保留数量（非 list 模式） */
  currentCount: number;
};

export type DiFiUploadChangePayload = {
  files: File[];
  fileList: FileList | null;
  nativeEvent: Event & { currentTarget: HTMLInputElement };
  /** 开启 `listType` 时，合并后列表中的全部文件 */
  allFiles?: File[];
  /** 通过 `maxSize` 校验的文件（本次选择中） */
  validFiles?: File[];
  /** 超过 `maxSize` 的文件 */
  rejectedByMaxSize?: File[];
  /** 超过 `maxCount` 未被接纳的文件 */
  rejectedByMaxCount?: File[];
};

export type DiFiUploadProps = {
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  name?: string;
  capture?: boolean | 'user' | 'environment';
  children?: JSX.Element;
  class?: string;
  style?: JSX.CSSProperties;
  /**
   * 上传触发区域宽度（点击选文件的外框）。
   * 支持 `number`（按 px）或 CSS 字符串，如 `'64px'`、`'4rem'`、`'50%'`。
   */
  width?: number | string;
  /**
   * 上传触发区域高度；写法同 `width`。
   * 与 `width` 同时设置时，内部占位内容会在区域内居中。
   */
  height?: number | string;

  triggerClass?: string;
  onChange?: (payload: DiFiUploadChangePayload) => void;
  resetAfterSelect?: boolean;
  /**
   * 列表展示形态；不传则不展示内建列表（仅选择器）。
   * - `text`：一行一项，文件名 + 删除
   * - `image`：小缩略图宫格（图片为缩略图，非图片为文件图标）
   * - `image-card`：较大卡片 + 底部文件名
   */
  listType?: DiFiUploadListType;
  /** 文件列表容器 class */
  listClass?: string;
  /**
   * 非图片文件（或无法生成缩略图的类型）的图标。
   * 优先于 `fileIconName`；返回节点会放在统一尺寸的占位区内。
   */
  fileIconRender?: (file: File) => JSX.Element;
  /**
   * 使用项目 `Icon` 注册名作为默认文件图标（如 `common-download`）。
   * 与 `fileIconRender` 同时存在时以 `fileIconRender` 为准。
   */
  fileIconName?: string;
  /** 是否展示删除；默认 true */
  listRemovable?: boolean;
  /** 列表变化（增删）时回调，参数为当前全部 `File` */
  onFilesUpdate?: (files: File[]) => void;
  /**
   * 列表内最大文件数量（含已选）；不传或非法值表示不限制。
   * list 模式：达到上限后禁用选择器；非 list 模式：单次选择最多保留 `maxCount` 个（`multiple` 时为上限，`!multiple` 时等价于最多 1 个）。
   */
  maxCount?: number;
  /**
   * 单文件最大字节数；不传或非法值表示不限制。
   * 例：`10 * 1024 * 1024` 表示 10MB。
   */
  maxSize?: number;
  /** 有文件超过 `maxSize` 时触发（可能一次多个） */
  onMaxSizeReject?: (payload: DiFiUploadMaxSizeRejectPayload) => void;
  /** 有文件因 `maxCount` 未被加入时触发 */
  onMaxCountExceed?: (payload: DiFiUploadMaxCountExceedPayload) => void;
  /**
   * 列表里缩略图旁/下是否展示文件名；默认 `false`。
   */
  showThumbnailName?: boolean;
  /**
   * 仍在上传的文件（与列表项是同一个 `File`）。缩略图上盖出金页同款转圈。
   */
  loadingFiles?: File[];
};

type UploadItem = {
  id: string;
  file: File;
  /** 图片预览 blob URL，卸载或删除时 revoke */
  objectUrl?: string;
};

function revokeItemPreview(it: UploadItem) {
  if (it.objectUrl) URL.revokeObjectURL(it.objectUrl);
}

function combineClass(...parts: (string | false | undefined | null)[]): string {
  return parts.filter(Boolean).join(' ');
}

function captureAttr(capture: DiFiUploadProps['capture']): Record<string, string | boolean> {
  if (capture === undefined) return {};
  if (capture === true) return { capture: true };
  return { capture };
}

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

function makeItemId(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 9)}`;
}

function effectiveMaxCount(n: number | undefined): number | undefined {
  if (n == null || !Number.isFinite(n)) return undefined;
  const k = Math.floor(n);
  return k < 0 ? undefined : k;
}

function effectiveMaxBytes(n: number | undefined): number | undefined {
  if (n == null || !Number.isFinite(n)) return undefined;
  return n <= 0 ? undefined : n;
}

function sizeToCss(v: number | string | undefined): string | undefined {
  if (v === undefined) return undefined;
  if (typeof v === 'number' && Number.isFinite(v)) return `${v}px`;
  if (typeof v === 'string' && v.trim() !== '') return v.trim();
  return undefined;
}

function partitionBySize(
  files: File[],
  maxBytes: number | undefined,
): { pass: File[]; overSize: File[] } {
  if (maxBytes == null) return { pass: [...files], overSize: [] };
  const pass: File[] = [];
  const overSize: File[] = [];
  for (const f of files) {
    if (f.size <= maxBytes) pass.push(f);
    else overSize.push(f);
  }
  return { pass, overSize };
}

function DefaultFileGlyph() {
  return (
    <svg class="difi-upload__svg-file" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"
      />
    </svg>
  );
}

export const DiFiUpload = (rawProps: DiFiUploadProps) => {
  const accept = rawProps.accept;
  const multiple = rawProps.multiple === true;
  const disabled = rawProps.disabled === true;
  const name = rawProps.name;
  const capture = rawProps.capture;
  const resetAfterSelect = rawProps.resetAfterSelect !== false;
  const listType = rawProps.listType ?? DEFAULT_LIST_TYPE;
  const listRemovable = rawProps.listRemovable !== false;
  const showList = listType != null;
  const showThumbnailName = rawProps.showThumbnailName ?? false;

  const [items, setItems] = createSignal<UploadItem[]>([]);

  onCleanup(() => {
    for (const it of items()) {
      revokeItemPreview(it);
    }
  });

  const allFiles = () => items().map((i) => i.file);

  /**
   * 合并本次已按 `maxSize` 过滤后的文件；应用 `maxCount`。
   * 返回合并后的列表及因数量被丢弃的文件。
   */
  const mergeIncomingFiles = (
    files: File[],
  ): { allFiles: File[]; rejectedByMaxCount: File[] } => {
    if (!showList) return { allFiles: [], rejectedByMaxCount: [] };
    if (files.length === 0) return { allFiles: allFiles(), rejectedByMaxCount: [] };

    const maxC = effectiveMaxCount(rawProps.maxCount);
    let next: UploadItem[] = [];
    let rejectedByMaxCount: File[] = [];

    setItems((prev) => {
      let base = prev;
      if (!multiple) {
        for (const it of prev) {
          revokeItemPreview(it);
        }
        base = [];
      }

      const room = maxC === undefined ? files.length : Math.max(0, maxC - base.length);
      const take = files.slice(0, room);
      rejectedByMaxCount = files.slice(room);

      const added: UploadItem[] = [];
      for (const file of take) {
        const id = makeItemId(file);
        let objectUrl: string | undefined;
        if (isImageFile(file)) {
          objectUrl = URL.createObjectURL(file);
        }
        added.push({ id, file, objectUrl });
      }
      next = [...base, ...added];
      return next;
    });

    if (rejectedByMaxCount.length && maxC != null) {
      rawProps.onMaxCountExceed?.({
        limit: maxC,
        dismissed: rejectedByMaxCount,
        currentCount: next.length,
      });
    }

    const out = next.map((i) => i.file);
    rawProps.onFilesUpdate?.(out);
    return { allFiles: out, rejectedByMaxCount };
  };

  const removeItem = (id: string) => {
    if (disabled || !listRemovable) return;
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.id === id);
      if (idx < 0) return prev;
      const it = prev[idx];
      revokeItemPreview(it);
      const next = prev.filter((_, i) => i !== idx);
      rawProps.onFilesUpdate?.(next.map((i) => i.file));
      return next;
    });
  };

  const onInputChange = (e: Event & { currentTarget: HTMLInputElement }) => {
    if (disabled || isAtMaxCount()) return;
    const target = e.currentTarget;
    const fileList = target.files;
    const files = fileList ? Array.from(fileList) : [];

    const maxBytes = effectiveMaxBytes(rawProps.maxSize);
    const { pass: sizeOk, overSize } = partitionBySize(files, maxBytes);
    if (overSize.length && maxBytes != null) {
      showToast(`文件大小超过${rawProps.maxSize / 1024 / 1024}MB`);
      rawProps.onMaxSizeReject?.({ maxBytes, files: overSize });
    }

    if (!showList) {
      const maxC = effectiveMaxCount(rawProps.maxCount);
      let valid = sizeOk;
      let rejectedByMaxCount: File[] = [];
      if (maxC != null) {
        const room = multiple ? maxC : Math.min(maxC, 1);
        rejectedByMaxCount = valid.slice(room);
        valid = valid.slice(0, room);
        if (rejectedByMaxCount.length) {
          rawProps.onMaxCountExceed?.({
            limit: maxC,
            dismissed: rejectedByMaxCount,
            currentCount: valid.length,
          });
        }
      }
      rawProps.onChange?.({
        files,
        fileList,
        nativeEvent: e,
        validFiles: valid,
        ...(overSize.length ? { rejectedByMaxSize: overSize } : {}),
        ...(rejectedByMaxCount.length ? { rejectedByMaxCount } : {}),
      });
      if (resetAfterSelect) {
        target.value = '';
      }
      return;
    }

    const { allFiles: allAfter, rejectedByMaxCount } = mergeIncomingFiles(sizeOk);

    rawProps.onChange?.({
      files,
      fileList,
      nativeEvent: e,
      allFiles: allAfter,
      validFiles: sizeOk,
      ...(overSize.length ? { rejectedByMaxSize: overSize } : {}),
      ...(rejectedByMaxCount.length ? { rejectedByMaxCount } : {}),
    });

    if (resetAfterSelect) {
      target.value = '';
    }
  };

  const renderFileIcon = (file: File) => {
    if (rawProps.fileIconRender) {
      return rawProps.fileIconRender(file);
    }
    if (rawProps.fileIconName) {
      return <Icon name={rawProps.fileIconName} class="icon-c-L" style={{ width: '28px', height: '28px' }} />;
    }
    return <DefaultFileGlyph />;
  };

  const isAtMaxCount = () => {
    const lim = effectiveMaxCount(rawProps.maxCount);
    return lim != null && items().length >= lim;
  };

  const boxW = sizeToCss(rawProps.width);
  const boxH = sizeToCss(rawProps.height);
  const hasTriggerBox = !!(boxW || boxH);

  /** 列表缩略图区域尺寸：与触发区 `width`/`height` 规则一致，单边则取正方形 */
  const thumbBox = (() => {
    if (boxW && boxH) {
      const s: JSX.CSSProperties = {
        width: boxW,
        height: boxH,
        'min-width': boxW,
        'min-height': boxH,
      };
      return { tw: boxW, th: boxH, style: s };
    }
    if (boxW) {
      const s: JSX.CSSProperties = {
        width: boxW,
        height: boxW,
        'min-width': boxW,
        'min-height': boxW,
      };
      return { tw: boxW, th: boxW, style: s };
    }
    if (boxH) {
      const s: JSX.CSSProperties = {
        width: boxH,
        height: boxH,
        'min-width': boxH,
        'min-height': boxH,
      };
      return { tw: boxH, th: boxH, style: s };
    }
    return { tw: undefined as string | undefined, th: undefined as string | undefined, style: undefined as JSX.CSSProperties | undefined };
  })();

  const triggerInnerStyle: JSX.CSSProperties | undefined = hasTriggerBox
    ? {
      position: 'relative',
      width: '100%',
      'border-style': 'dashed',
      'box-sizing': 'border-box',
      ...(boxH
        ? {
          flex: '1 1 0%',
          minHeight: 0,
          display: 'flex',
          'justify-content': 'center',
          'align-items': 'center',
        }
        : {}),
    }
    : undefined;

  const labelClass = () =>
    combineClass(
      'difi-upload',
      disabled && 'difi-upload--disabled',
      hasTriggerBox && 'difi-upload--sized',
      showList ? undefined : rawProps.class,
    );

  const labelStyle: JSX.CSSProperties = {
    ...(showList ? {} : { ...rawProps.style }),
    ...(boxW ? { width: boxW, minWidth: boxW } : {}),
    ...(boxH ? { height: boxH, minHeight: boxH } : {}),
    ...(boxH
      ? {
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      }
      : {}),
  };

  const wrapClass = () =>
    combineClass('difi-upload-wrap width-100', showList ? rawProps.class : undefined);

  const wrapStyle: JSX.CSSProperties = showList ? { ...rawProps.style } : {};

  const triggerClass = combineClass('difi-upload__trigger', rawProps.triggerClass);

  const defaultTrigger = (
    <span class={combineClass(triggerClass, 'difi-upload__trigger--add')} style={triggerInnerStyle}>
      <svg class="difi-upload__plus" viewBox="0 0 14 14" aria-hidden="true">
        <path d="M7 2V12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M2 7H12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </span>
  );

  const trigger =
    rawProps.children != null ? (
      <span class={triggerClass} style={triggerInnerStyle}>
        {rawProps.children}
      </span>
    ) : (
      defaultTrigger
    );

  const listRootClass = () =>
    combineClass(
      'difi-upload__list',
      listType === 'text' && 'difi-upload__list--text',
      listType === 'image' && 'difi-upload__list--image',
      listType === 'image-card' && 'difi-upload__list--image-card',
      rawProps.listClass,
    );

  const RemoveBtn = (p: { variant: 'text' | 'image' | 'card'; id: string }) => {
    if (!listRemovable) return null;
    const cls =
      p.variant === 'text'
        ? 'difi-upload__remove'
        : p.variant === 'image'
          ? 'difi-upload__remove difi-upload__remove--float'
          : 'difi-upload__remove difi-upload__remove--card';
    return (
      <button
        type="button"
        class={cls}
        disabled={disabled}
        aria-label="移除"
        onclick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          removeItem(p.id);
        }}
      >
        <img class="difi-upload__remove-icon" src={removeIcon} alt="" />
      </button>
    );
  };

  const loadingMask = () => (
    <div class="difi-upload__loading bg-c-XD flex align-center justify-center">
      <span class="difi-upload__spin" />
    </div>
  );

  const renderTextItem = (it: UploadItem, busy: boolean) => {
    const img = isImageFile(it.file) && it.objectUrl;
    return (
      <li class="difi-upload__item--text padding-S round-S bg-c-XL">
        <div class="difi-upload__preview--text" style={thumbBox.style}>
          {img ? <img src={it.objectUrl} alt="" /> : renderFileIcon(it.file)}
          {busy ? loadingMask() : null}
        </div>
        {showThumbnailName ? (
          <div class="difi-upload__name--text single-line-ellipsis color-D font-s-S">{it.file.name}</div>
        ) : null}
        <RemoveBtn variant="text" id={it.id} />
      </li>
    );
  };

  const renderImageItem = (it: UploadItem, busy: boolean) => {
    const img = isImageFile(it.file) && it.objectUrl;
    const nameMaxW = thumbBox.tw ?? thumbBox.th;
    return (
      <li
        class={combineClass(
          'difi-upload__item--image-outer',
          showThumbnailName && 'difi-upload__item--image-outer--with-name',
        )}
        style={nameMaxW ? { width: nameMaxW, 'max-width': '100%' } : undefined}
      >
        <div class="difi-upload__item--image" style={thumbBox.style}>
          {img ? (
            <img src={it.objectUrl} alt="" />
          ) : (
            <div class="difi-upload__preview--image-icon">{renderFileIcon(it.file)}</div>
          )}
          {busy ? loadingMask() : null}
          <RemoveBtn variant="image" id={it.id} />
        </div>
        {showThumbnailName ? (
          <div
            class="difi-upload__name--image single-line-ellipsis color-L font-s-S text-center width-100"
            style={nameMaxW ? { 'max-width': nameMaxW } : undefined}
          >
            {it.file.name}
          </div>
        ) : null}
      </li>
    );
  };

  const renderCardItem = (it: UploadItem, busy: boolean) => {
    const img = isImageFile(it.file) && it.objectUrl;
    const cardW = thumbBox.tw;
    return (
      <li
        class="difi-upload__item--card"
        style={cardW ? { width: cardW, 'max-width': '100%' } : undefined}
      >
        <div class="difi-upload__card-box round-XL bg-c-XXD" style={thumbBox.style}>
          {img ? (
            <img src={it.objectUrl} alt="" />
          ) : (
            <div class="difi-upload__preview--card-icon">{renderFileIcon(it.file)}</div>
          )}
          {busy ? loadingMask() : null}
          <RemoveBtn variant="card" id={it.id} />
        </div>
        {showThumbnailName ? (
          <div
            class="difi-upload__name--card single-line-ellipsis"
            style={cardW ? { 'max-width': cardW } : undefined}
          >
            {it.file.name}
          </div>
        ) : null}
      </li>
    );
  };

  const listBody = () => {
    if (!showList) return null;
    const loadingFiles = rawProps.loadingFiles || [];
    return (
      <ul class={listRootClass()}>
        <For each={items()}>
          {(it) => {
            const busy = loadingFiles.indexOf(it.file) >= 0;
            if (listType === 'text') return renderTextItem(it, busy);
            if (listType === 'image') return renderImageItem(it, busy);
            return renderCardItem(it, busy);
          }}
        </For>
      </ul>
    );
  };

  const labelBlock = (
    <label class={labelClass()} style={labelStyle}>
      <input
        class="difi-upload__input"
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled || isAtMaxCount()}
        name={name}
        {...captureAttr(capture)}
        onchange={onInputChange}
      />
      {trigger}
    </label>
  );

  if (!showList) {
    return labelBlock;
  }

  return (
    <div class={wrapClass()} style={wrapStyle}>
      {listBody()}
      {/* 已达 maxCount：隐藏加号，避免 disabled 半透明误以为还能点 */}
      {isAtMaxCount() ? null : labelBlock}
    </div>
  );
};
